/* eslint-disable */
import React from 'react';
import { styles } from './CatalogBudgetPrintable.styles';

// Componente renderizado fora da tela para geração do PDF
// Espera props.data com: { name, informationsClient, informations, validity, products: [{name, quantity, unitValue, totalValue}] }
const CatalogBudgetPrintable = ({ data }) => {
  const products = Array.isArray(data?.products) ? data.products : [];
  // Parser robusto para strings de moeda no padrão BR/US
  const parseMoney = (val) => {
    if (val === null || val === undefined) return 0;
    if (typeof val === 'number' && !Number.isNaN(val)) return val;
    const s = String(val).trim();
    if (!s) return 0;
    // Remove símbolo R$, espaços
    let cleaned = s.replace(/[^0-9.,-]/g, '');
    // Se tiver separador de milhar '.' e decimal ',' (pt-BR): remove '.' e troca ',' por '.'
    if (cleaned.includes(',') && cleaned.includes('.')) {
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else if (cleaned.includes(',')) {
      // Se só tiver vírgula, assume vírgula como decimal
      cleaned = cleaned.replace(',', '.');
    }
    const n = parseFloat(cleaned);
    if (Number.isNaN(n)) return 0;
    return n;
  };

  // Preferir o subtotal já calculado pelo formulário (após descontos)
  const subtotal =
    typeof data?.subtotalFromItems === 'number'
      ? data.subtotalFromItems
      : products.reduce((acc, p) => acc + (parseMoney(p.totalValue) || 0), 0);

  // Formatar data e hora atual
  const now = new Date();
  const dateTimeFormatted = now.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });

  const renderClientInfo = () => {
    if (data?.clientData) {
      const {
        account = {},
        contact = {},
        customFieldsLabels = {},
        template = ''
      } = data.clientData;

      if (template && typeof template === 'string') {
        const variablePattern = /\{\{([^}]+)\}\}/g;
        const fieldsToRender = [];
        let match;

        while ((match = variablePattern.exec(template)) !== null) {
          const variable = match[1].trim();
          let label = '';
          let value = null;

          if (variable.startsWith('account.')) {
            const field = variable.substring('account.'.length);
            if (field === 'phone') {
              const ddi = account.ddi ? `+${account.ddi}` : '';
              const phone = account.phone || '';
              value = ddi && phone ? `${ddi} ${phone}` : phone || ddi || null;
            } else if (field === 'customerPortfolioId') {
              value = account.customerPortfolioName || account[field] || null;
            } else {
              value = account[field] ?? null;
            }
            label =
              customFieldsLabels[field] ||
              field.charAt(0).toUpperCase() + field.slice(1);
          } else if (variable.startsWith('contact.')) {
            const field = variable.substring('contact.'.length);
            if (field === 'phone') {
              const ddi = contact.ddi ? `+${contact.ddi}` : '';
              const phone = contact.phone || '';
              value = ddi && phone ? `${ddi} ${phone}` : phone || ddi || null;
            } else {
              value = contact[field] ?? null;
            }
            label =
              customFieldsLabels[field] ||
              field.charAt(0).toUpperCase() + field.slice(1);
          }

          if (label && value !== null && value !== '') {
            fieldsToRender.push({ label, value });
          }
        }

        return fieldsToRender;
      }

      const standardFields = [];
      if (account.name)
        standardFields.push({ label: 'Conta', value: account.name });
      if (account.cnpj)
        standardFields.push({ label: 'CNPJ', value: account.cnpj });
      if (contact.name)
        standardFields.push({ label: 'Contato', value: contact.name });
      if (contact.email)
        standardFields.push({ label: 'E-mail', value: contact.email });

      const phoneAccount =
        account.ddi && account.phone
          ? `+${account.ddi} ${account.phone}`
          : account.phone;
      const phoneContact =
        contact.ddi && contact.phone
          ? `+${contact.ddi} ${contact.phone}`
          : contact.phone;

      if (phoneAccount)
        standardFields.push({ label: 'Telefone (Conta)', value: phoneAccount });
      if (phoneContact && phoneContact !== phoneAccount) {
        standardFields.push({
          label: 'Telefone (Contato)',
          value: phoneContact
        });
      }

      return standardFields;
    }

    if (data?.informationsClient) {
      const lines = String(data.informationsClient)
        .split('\n')
        .filter((line) => line.trim());

      return lines.map((line) => {
        const sepIndex = line.indexOf(':');
        if (sepIndex > -1) {
          const label = line.substring(0, sepIndex).trim();
          const value = line.substring(sepIndex + 1).trim();
          return { label, value, isLegacy: true };
        }
        return { label: '', value: line, isLegacy: true };
      });
    }

    return [];
  };

  const clientInfoFields = renderClientInfo();

  return (
    <div style={styles.container}>
      {/* Logo da proposta (se existir) */}
      {data?.logoUrl && (
        <div style={{ marginBottom: 20, textAlign: 'left' }}>
          <img
            src={data.logoUrl}
            alt="Logo"
            crossOrigin="anonymous"
            style={{
              maxWidth: '200px',
              maxHeight: '80px',
              objectFit: 'contain'
            }}
          />
        </div>
      )}

      {/* Informações antes do título (alinhadas à direita) */}
      {(data?.informations || data?.validity) && (
        <div style={styles.infoBeforeTitle}>
          {data?.informations && (
            <div style={{ marginBottom: 5 }}>
              {String(data.informations)
                .split('\n')
                .map((line, idx) => (
                  <div key={idx}>{line}</div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Título usando a variável title da proposta */}
      <div style={styles.title}>{data?.title || 'ORÇAMENTO'}</div>

      <div style={styles.sectionTitle}>Informações do Cliente</div>
      <div style={styles.clientInfo}>
        {clientInfoFields.length > 0 ? (
          clientInfoFields.map((field, idx) => (
            <div key={idx} style={styles.clientInfoLine}>
              {field.value}
            </div>
          ))
        ) : (
          <div>Nenhuma informação disponível</div>
        )}
      </div>

      <div style={styles.sectionTitle}>Itens do Orçamento</div>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.thProduct}>Produto</th>
            <th style={styles.thQuantity}>Quant.</th>
            <th style={styles.thUnitValue}>Valor Unit.</th>
            <th style={styles.thTotalValue}>Valor Total</th>
          </tr>
        </thead>
        <tbody>
          {products.length > 0 ? (
            products.map((p, idx) => (
              <tr key={idx}>
                <td style={styles.td}>{p.name || p.productName || ''}</td>
                <td style={{ ...styles.td, ...styles.center }}>
                  {p.quantity || 0}
                </td>
                <td style={{ ...styles.td, ...styles.right }}>
                  R$ {parseMoney(p.unitValue).toFixed(2)}
                </td>
                <td style={{ ...styles.td, ...styles.right }}>
                  R$ {parseMoney(p.totalValue).toFixed(2)}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td style={{ ...styles.td, ...styles.center }} colSpan={4}>
                Nenhum produto adicionado
              </td>
            </tr>
          )}
          <tr>
            <td
              style={{ ...styles.td, ...styles.right, ...styles.subtotalRow }}
              colSpan={3}
            >
              Subtotal:
            </td>
            <td
              style={{ ...styles.td, ...styles.right, ...styles.subtotalRow }}
            >
              R$ {subtotal.toFixed(2)}
            </td>
          </tr>
        </tbody>
      </table>

      <div style={styles.sectionTitle}>Informações do Orçamento</div>
      <div style={styles.budgetInfo}>
        <div>
          <span style={styles.label}>
            Valor do Orçamento (Taxas, Fretes, taxas e descontos):
          </span>{' '}
          R${' '}
          {(typeof data?.finalValueWithTaxesFromForm === 'number'
            ? data.finalValueWithTaxesFromForm
            : typeof data?.finalValueFromForm === 'number'
            ? data.finalValueFromForm
            : subtotal
          ).toFixed(2)}
        </div>
        <div>
          <span style={styles.label}>Validade do Orçamento:</span>{' '}
          {data?.validity ? `${data.validity} dias` : ''}
        </div>
      </div>

      {/* Rodapé com responsável e data/hora */}

      <div style={styles.footer}>
        <div style={styles.sectionTitle}>Informações Adicionais</div>
        <div style={{ marginBottom: 5 }}>
          <strong>Responsável:</strong>{' '}
          {data?.responsibleName || localStorage.getItem('name') || 'N/A'}
        </div>
        <div>
          <strong>Data e Hora:</strong> {dateTimeFormatted}
        </div>
      </div>
    </div>
  );
};

export default CatalogBudgetPrintable;
